import React from 'react';

import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import makeFullName from 'common/util/makeFullName';
import getNullEmptyValue from 'common/util/getNullEmptyValue';
import { formatPrice } from 'common/formatters';

import {
  UTILITY_INVOICE_STATUS_CANCELLED,
  UTILITY_INVOICE_STATUS_PAID,
  UTILITY_INVOICE_STATUS_LIST,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ALIASES,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER,
  UTILITY_ACTIVATION_ITEM_STATUS_LIST,
  UTILITY_ACTIVATION_STATUS_LIST,
  UTILITY_ACTIVATION_STATUS_CANCEL_REASON_LIST,
} from 'common/utility/constants';

import {
  SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
  SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE,
  SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE,
  SR_TABLE_COLUMN_FILTER_TYPE_TEXT,
  SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_INSTANT,
} from 'client/components/SrTableColumnFilter';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
  SR_API_ACTION_UTILITY_INVOICE_UPDATE,
  SR_API_ACTION_UTILITY_PAYOUT_UPDATE,
} from 'api/utilityActionTypes';

import { showFieldIf, transformBooleanToYesNo } from 'client/helpers/formikHelpers';

import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';

// Builds Excel-style multi-select option lists for categorical filters whose values
// are camelCase enum strings (e.g. 'missingProvider' → 'Missing Provider').
const enumOptions = (list) => list.map((value) => ({ value, label: camelCaseToTitleCase(value) }));

// Three-state "Paid by ISP?" filter. Backend computes `isPaidByIsp` as a string
// enum ('paid' | 'cancelled' | 'no') from the underlying invoiceStatus, so each
// option here maps 1:1 to a value the BE can match via `IN (...)`. Earlier this
// column was boolean and the UI sent string values, which silently broke the
// filter at the SQL layer.
const PAID_BY_ISP_FILTER_OPTIONS = [
  { value: UTILITY_INVOICE_STATUS_PAID, label: 'Yes' },
  { value: UTILITY_INVOICE_STATUS_CANCELLED, label: 'Cancelled' },
  { value: 'no', label: 'No' },
];

// "Paid Partner?" is a true boolean. Yes/No covers the meaningful states.
const YES_NO_FILTER_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

// TODO(@mathew-ship-it): replace text-match filters on partnerName / providerName with
// categorical multi-select once `/datatables/distinct-values?source=...&column=...` (or an
// equivalent partner/provider list endpoint) is exposed by the UP API. Tracked separately
// from FEAT-B40 because it requires a backend addition that does not block this rollout.
export function makeAccountingDashboardRows({
  rows,
  utilityDealers,
  utilitySalesReps,
  onEditableClick,
  updateActivationItem,
  highlightRowId,
  setHighlightRowId,
}) {
  return rows.map((row) => ({
    onClick: () => {
      setHighlightRowId(row.id);
    },
    className: highlightRowId === row.id ? 'highlighted' : '',
    cells: [
      {
        dataTableFieldId: 'utilitySalesRepName',
        label: 'Sales Rep',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: (utilitySalesReps || []).map(({ firstName, lastName }) => ({
            value: makeFullName(firstName, lastName),
            label: makeFullName(firstName, lastName),
          })),
        },
        editable: {
          getFormFields: () => {
            return [
              {
                title: 'Sales Rep',
                id: 'utilitySalesRepId',
                type: 'select',
                properties: {
                  placeholder: '--',
                  enableResetOption: true,
                  choices: (utilitySalesReps || []).map(({ id, firstName, lastName }) => ({
                    label: makeFullName(firstName, lastName),
                    value: id,
                  })),
                },
              },
            ];
          },
          onSubmit: (formValues) => {
            updateActivationItem({
              actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
              itemId: row.utilityActivationItemId,
              payload: {
                id: row.utilityActivationId,
                updates: {
                  utilitySalesRepId: parseInt(formValues.utilitySalesRepId, 10) || null,
                },
              },
            });
          },
          defaultValues: {
            utilitySalesRepId: String(row.utilitySalesRepId),
          },
        },
      },
      {
        dataTableFieldId: 'renterName',
        label: 'Renter Name',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search name…' },
      },
      {
        dataTableFieldId: 'renterNumber',
        label: 'Renter Number',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search number…' },
      },
      {
        dataTableFieldId: 'partnerName',
        label: 'Partner',
        // Categorical preferred (locked Q3); using text-match until partner-list endpoint exists.
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search partner…' },
      },
      {
        dataTableFieldId: 'addressStreetDisplay',
        label: 'Address',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search address…' },
      },
      {
        dataTableFieldId: 'addressStateCode',
        label: 'State',
        // 2-letter state codes; text filter is sufficient and avoids a static 50-state list.
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'e.g. TX' },
      },
      {
        dataTableFieldId: 'accountRequestedAt',
        label: 'Purchase Date',
        // accountRequestedAt is a TIMESTAMPTZ column (true instant). The
        // dashboard renders it in Austin time including a wall-clock time
        // (`MM/DD/YYYY at h:mmA`), so the operator's filter boundary is
        // mentally Austin midnight. Use 'instant' so the wire format is an
        // Austin-tz ISO timestamp and the backend's `>=` / `<=` comparisons
        // line up with the displayed dates regardless of how the row's UTC
        // timestamp falls relative to UTC midnight.
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE,
          dateInputMode: SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_INSTANT,
        },
        value: row.accountRequestedAt
          ? momentWithAustinTimezone(row.accountRequestedAt).format('MM/DD/YYYY [at] h:mmA')
          : '--',
      },
      {
        dataTableFieldId: 'leaseStartDate',
        label: 'Move-In Date / Installation Date',
        // leaseStartDate is a DATE column (no time component). Send YYYY-MM-DD
        // straight through — Postgres compares dates directly so the operator
        // sees the exact range they picked. Sending an Austin-tz ISO timestamp
        // here is what produced QA's "shows May 2-8 instead of May 1-7" report:
        // Postgres cast the ISO string to DATE in the session timezone, which
        // shifted both boundaries by one day for non-Austin sessions. The
        // default 'date' mode is the safe, historical behavior.
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE },
      },
      {
        dataTableFieldId: 'providerName',
        label: 'Provider',
        // Categorical preferred (locked Q3); using text-match until provider-list endpoint exists.
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search provider…' },
      },
      {
        dataTableFieldId: 'providerPlanInternetSpeedMbps',
        label: 'Speed mbps',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE },
      },
      {
        dataTableFieldId: 'providerPlanName',
        label: 'Package',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search package…' },
        value: `${row.providerPlanName} - ${row.utilityProviderPlanId}`,
      },
      {
        // UPT Link is a derived UI element with no underlying data column. Per the locked
        // "every column gets controls" decision, we still expose a text filter that targets
        // the underlying activation short ID — useful when an operator has a UPT link in
        // hand and wants to find the matching row.
        dataTableFieldId: 'utilityActivationShortId',
        label: 'UPT Link',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Activation ID' },
        content: (
          <SrLink
            to={
              `/utility-profit/admin/processing/${row.utilityActivationShortId}` +
              `?backUrl=${encodeURIComponent('/utility-profit/admin/dashboard/accounting')}` +
              `&backTitle=${encodeURIComponent('Back to Accounting Dashboard')}`
            }
            target="_blank"
          >
            <SrText
              font="greycliff"
              color="blue300"
              cursor="pointer"
              weight="800"
              fontSize="16px"
              lineHeight="50px"
            >
              Link
            </SrText>
          </SrLink>
        ),
      },
      {
        dataTableFieldId: 'dealerName',
        label: 'Dealer',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          // Filter against the FULL dealer list (not row-scoped); operators may filter to
          // dealers that don't apply to a particular plan.
          options: (utilityDealers || []).map(({ name }) => ({ value: name, label: name })),
        },
        editable: {
          getFormFields: () => {
            const availableDealersList = utilityDealers.filter(({ id, utilityProviderPlanIds }) => {
              return (
                row.utilityDealerId === id ||
                (row.utilityProviderPlanId &&
                  utilityProviderPlanIds.includes(row.utilityProviderPlanId))
              );
            });

            return [
              {
                title: 'Dealer',
                id: 'utilityDealerId',
                type: 'select',
                properties: {
                  placeholder: !row.utilityProviderPlanId
                    ? 'No plan has been selected yet.'
                    : availableDealersList.length === 0
                    ? 'No dealer portals have been set up for this plan.'
                    : '--',
                  enableResetOption: true,
                  choices: (availableDealersList || []).map(({ id, name }) => ({
                    label: name,
                    value: id,
                  })),
                },
              },
            ];
          },
          onSubmit: (formValues) => {
            updateActivationItem({
              actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
              itemId: row.utilityActivationItemId,
              payload: {
                id: row.utilityActivationItemId,
                updates: formValues,
              },
            });
          },
          defaultValues: {
            utilityDealerId: row.utilityDealerId,
          },
        },
      },
      {
        dataTableFieldId: 'utilityDealerExternalPurchaseId',
        label: 'Purchase ID',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search ID…' },
        editable: 'short_text',
      },
      {
        dataTableFieldId: 'commissionAmount',
        label: 'Expected Revenue to UP',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE },
        value: typeof row.commissionAmount === 'number' ? formatPrice(row.commissionAmount) : '--',
      },
      {
        dataTableFieldId: 'invoiceAmount',
        label: 'Actual Revenue to UP',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE },
        value: typeof row.invoiceAmount === 'number' ? formatPrice(row.invoiceAmount) : '--',
        editable: !row.invoiceId
          ? null
          : {
              getFormFields: () => [
                {
                  id: 'amount',
                  label: 'Invoice Amount',
                  type: 'currency',
                  validations: ['required'],
                },
              ],
              onSubmit: (formValues) => {
                updateActivationItem({
                  actionType: SR_API_ACTION_UTILITY_INVOICE_UPDATE,
                  itemId: row.utilityActivationItemId,
                  payload: {
                    id: row.invoiceId,
                    updates: formValues,
                  },
                });
              },
              defaultValues: {
                amount: row.invoiceAmount,
              },
            },
      },
      {
        dataTableFieldId: 'payoutAmount',
        label: 'Payout to Partner',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE },
        value: typeof row.payoutAmount === 'number' ? formatPrice(row.payoutAmount) : '--',
        editable: !row.payoutId
          ? null
          : {
              getFormFields: () => [
                {
                  id: 'amount',
                  label: 'Payout Amount',
                  type: 'currency',
                  validations: ['required'],
                },
              ],
              onSubmit: (formValues) => {
                updateActivationItem({
                  actionType: SR_API_ACTION_UTILITY_PAYOUT_UPDATE,
                  itemId: row.utilityActivationItemId,
                  payload: {
                    id: row.payoutId,
                    updates: formValues,
                  },
                });
              },
              defaultValues: {
                amount: row.payoutAmount,
              },
            },
      },
      {
        dataTableFieldId: 'activationStatus',
        label: 'Activation Status',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: enumOptions(UTILITY_ACTIVATION_STATUS_LIST),
        },
        value: camelCaseToTitleCase(row.activationStatus),
      },
      {
        dataTableFieldId: 'activationCancelReason',
        label: 'Activation Cancel Reason',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: enumOptions(UTILITY_ACTIVATION_STATUS_CANCEL_REASON_LIST),
        },
        value: camelCaseToTitleCase(row.activationCancelReason),
      },
      {
        dataTableFieldId: 'activationItemStatus',
        label: 'Purchase Request Status',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: enumOptions(UTILITY_ACTIVATION_ITEM_STATUS_LIST),
        },
        value: camelCaseToTitleCase(row.activationItemStatus),
      },
      {
        dataTableFieldId: 'dropoutReason',
        label: 'Renter Cancelled',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: enumOptions(UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST),
        },
        value:
          camelCaseToTitleCase(
            UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ALIASES[row.dropoutReason] || row.dropoutReason,
          ) || '--',
        editable: {
          getFormFields: (formValues) => [
            {
              title: 'Cancel reason?',
              id: 'dropoutReason',
              type: 'select',
              properties: {
                placeholder: 'Not Cancelled',
                enableResetOption: true,
                choices: UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST.map((value) => ({
                  label: camelCaseToTitleCase(value),
                  value,
                })),
              },
            },
            showFieldIf(formValues?.dropoutReason, {
              title:
                formValues?.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
                  ? 'Other reason'
                  : `"${camelCaseToTitleCase(formValues?.dropoutReason)}" explanation`,
              id: 'dropoutReasonExplanation',
              type: 'long_text',
              properties: {
                placeholder: 'Write here...',
              },
              validations:
                formValues?.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
                  ? ['required']
                  : [],
            }),
          ],
          onSubmit: (formValues) => {
            updateActivationItem({
              actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
              itemId: row.utilityActivationItemId,
              payload: {
                id: row.utilityActivationItemId,
                updates: formValues,
              },
            });
          },
          defaultValues: {
            // Rows archived through the utilityprofit Order Placer tool can
            // carry its legacy snake_case spelling; normalize so the select matches.
            dropoutReason:
              UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ALIASES[row.dropoutReason] ||
              row.dropoutReason,
            dropoutReasonExplanation: row.dropoutReasonExplanation,
          },
        },
      },
      {
        dataTableFieldId: 'isPaidByIsp',
        label: 'Paid by ISP?',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: PAID_BY_ISP_FILTER_OPTIONS,
        },
        value:
          row.invoiceStatus === UTILITY_INVOICE_STATUS_CANCELLED
            ? 'Cancelled'
            : row.invoiceStatus === UTILITY_INVOICE_STATUS_PAID
            ? 'Yes'
            : 'No',
        editable: !row.invoiceId
          ? null
          : {
              getFormFields: (formValues) => [
                {
                  id: 'status',
                  label: 'Invoice Status',
                  type: 'select',
                  properties: {
                    choices: UTILITY_INVOICE_STATUS_LIST.map((status) => ({
                      label: camelCaseToTitleCase(status),
                      value: status,
                    })),
                  },
                  validations: ['required'],
                },
                showFieldIf(formValues?.status === UTILITY_INVOICE_STATUS_CANCELLED, {
                  title: 'Cancel Reason',
                  id: 'cancelReason',
                  type: 'long_text',
                  properties: {
                    placeholder: 'Write here...',
                  },
                  validations: ['required'],
                }),
              ],
              onSubmit: (formValues) => {
                updateActivationItem({
                  actionType: SR_API_ACTION_UTILITY_INVOICE_UPDATE,
                  itemId: row.utilityActivationItemId,
                  payload: {
                    id: row.invoiceId,
                    updates: {
                      status: formValues.status,
                      cancelReason:
                        formValues.status === UTILITY_INVOICE_STATUS_CANCELLED
                          ? formValues.cancelReason
                          : null,
                    },
                  },
                });
              },
              defaultValues: {
                status: row.invoiceStatus,
                cancelReason: row.invoiceCancelReason,
              },
            },
      },
      {
        dataTableFieldId: 'isPartnerPaid',
        label: 'Paid Partner?',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: YES_NO_FILTER_OPTIONS,
        },
      },
    ].map((cell) => {
      const rawValue = 'value' in cell ? cell.value : row[cell.dataTableFieldId];
      const editable = !cell.editable
        ? null
        : typeof cell.editable !== 'string'
        ? cell.editable
        : {
            getFormFields: () => [
              {
                id: cell.dataTableFieldId,
                label: cell.label,
                type: cell.editable,
                validations: ['required'],
              },
            ],
            onSubmit: (formValues) => {
              updateActivationItem({
                actionType: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
                itemId: row.utilityActivationItemId,
                payload: {
                  id: row.utilityActivationItemId,
                  updates: formValues,
                },
              });
            },
            defaultValues: {
              [cell.dataTableFieldId]: row[cell.dataTableFieldId],
            },
          };

      const onClick = editable ? () => onEditableClick(editable) : null;
      let content = cell.content
        ? cell.content
        : typeof rawValue === 'boolean'
        ? transformBooleanToYesNo(rawValue, true)
        : getNullEmptyValue(rawValue, '--');

      if (onClick) {
        cell.className = 'clickable';
        content = (
          <SrText underlineOnHover font="greycliff" size="16" weight="800">
            {content}
          </SrText>
        );
      }

      return {
        ...cell,
        content,
        onClick,
      };
    }),
  }));
}

/**
 * Builds the header config for the Internet accounting dashboard table.
 *
 * Headers are derived from the cell config so column order, ids, and labels stay in lockstep
 * with the row data — there is no separate header definition to drift. Filter configs flow
 * through unchanged. The Sales Rep and Dealer filters need the live `utilitySalesReps` and
 * `utilityDealers` lists to populate categorical options, so this is a function rather than
 * a module-load constant.
 */
export function getAccountingDashboardHeaders({ utilityDealers, utilitySalesReps } = {}) {
  const syntheticCells = makeAccountingDashboardRows({
    rows: [{}],
    utilityDealers,
    utilitySalesReps,
  })[0].cells;
  return syntheticCells.map(({ dataTableFieldId, label, filterConfig }) => ({
    dataTableFieldId,
    content: label,
    filterConfig,
  }));
}

// Backwards-compat shim — kept for any existing callers; new code should call
// `getAccountingDashboardHeaders(props)` so categorical filter options get live data.
export const accountingDashboardHeaders = getAccountingDashboardHeaders();
